import { Icon } from '@amzn/awsui-components-core';
import { findIconDefinition, IconLookup, library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PARENT_TO_EMBED_MESSAGES } from 'src/constants/EmbedMessageConstants';
import DFP_LOGO_TEXT from '../../../images/finsuite-text.svg';
import ARROW_ICON from '../../../images/arrow.svg';
import { LogoIcon, LOGO_PALETTES } from './LogoIcon';
import { LogoConfig } from '../../context/AppContextModels';
import { AppLogoService } from 'src/services/AppLogoService';
import type { EmbeddedAppState } from 'src/hooks/useEmbeddedApp';

library.add(fas);

/**
 * Renders an app logo from a LogoConfig. Supports three sources:
 * - Cloudscape icon (prefix `cloudscape:`)
 * - FontAwesome solid icon (prefix `fa:`)
 * - S3-hosted image (fetched via presigned URL)
 */
export const AppLogo: React.FC<{ logoConfig: LogoConfig }> = ({ logoConfig }) => {
  const [s3LogoUrl, setS3LogoUrl] = useState<string | null>(null);
  const s3LogoUrlRef = useRef<string | null>(null);
  const { selectedLogoName, s3Location } = logoConfig;

  useEffect(() => {
    if (!s3Location) return;
    let cancelled = false;
    AppLogoService.getLogoPreviewUrl(s3Location).then((url) => {
      if (!cancelled && url) {
        if (s3LogoUrlRef.current) URL.revokeObjectURL(s3LogoUrlRef.current);
        s3LogoUrlRef.current = url;
        setS3LogoUrl(url);
      } else if (url) {
        URL.revokeObjectURL(url);
      }
    });
    return () => {
      cancelled = true;
      if (s3LogoUrlRef.current) {
        URL.revokeObjectURL(s3LogoUrlRef.current);
        s3LogoUrlRef.current = null;
      }
    };
  }, [s3Location?.key]);

  if (selectedLogoName?.startsWith('cloudscape:')) {
    return (
      <Icon
        name={selectedLogoName.replace('cloudscape:', '') as import('@amzn/awsui-components-core/icon').IconProps.Name}
        size="medium"
        variant="inverted"
      />
    );
  }

  if (selectedLogoName?.startsWith('fa:')) {
    const lookup: IconLookup = { prefix: 'fas', iconName: selectedLogoName.replace('fa:', '') as any };
    const def = findIconDefinition(lookup);
    return def ? <FontAwesomeIcon icon={def} className="dfp-top-nav__app-logo-fa" /> : null;
  }

  if (s3LogoUrl) {
    return <img src={s3LogoUrl} alt="App logo" className="dfp-top-nav__app-logo-img" />;
  }

  return null;
};

/**
 * Renders a static app logo from explicit values (no S3 fetch).
 * Used in preview mode where the presigned URL is already resolved.
 */
export const AppLogoStatic: React.FC<{ selectedLogoName: string | null; previewUrl: string | null }> = ({ selectedLogoName, previewUrl }) => {
  if (selectedLogoName?.startsWith('cloudscape:')) {
    return (
      <Icon
        name={selectedLogoName.replace('cloudscape:', '') as import('@amzn/awsui-components-core/icon').IconProps.Name}
        size="medium"
        variant="inverted"
      />
    );
  }

  if (selectedLogoName?.startsWith('fa:')) {
    const lookup: IconLookup = { prefix: 'fas', iconName: selectedLogoName.replace('fa:', '') as any };
    const def = findIconDefinition(lookup);
    return def ? <FontAwesomeIcon icon={def} className="dfp-top-nav__app-logo-fa" /> : null;
  }

  if (previewUrl) {
    return <img src={previewUrl} alt="App logo" className="dfp-top-nav__app-logo-img" />;
  }

  return null;
};

/**
 * TopNavLogo receives all embedded app state from the parent DFPTopNavigation
 * to avoid duplicate hook instances and divergent readiness state.
 */
export const TopNavLogo: React.FC<EmbeddedAppState> = ({ embeddedApp, embedMatch, sendMessageToEmbed, displayAppName, routeConfig }) => {
  const navigate = useNavigate();

  const handleEmbeddedAppHomeNavigation = useCallback(() => {
    if (sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.NAVIGATE_HOME)) return;
    if (embedMatch?.params?.id) {
      navigate(`/all-apps/${embedMatch.params.id}?t=${Date.now()}`);
    }
  }, [embedMatch?.params?.id, navigate, sendMessageToEmbed]);

  const isEmbedded = !!embeddedApp && !!embedMatch?.params?.id;
  const isOrgToolRoute = !isEmbedded && !!displayAppName && routeConfig?.navigationSection === 'org-tools';
  const showEmbeddedStyle = isEmbedded || isOrgToolRoute;
  const currentEmbedId = embedMatch?.params?.id ?? null;
  const currentRouteKey = isOrgToolRoute ? routeConfig?.path ?? null : currentEmbedId;

  const isInitialMount = useRef(true);
  const prevEmbedded = useRef(showEmbeddedStyle);
  const prevEmbedId = useRef(currentRouteKey);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [textHidden, setTextHidden] = useState(showEmbeddedStyle);
  const [logoFrame, setLogoFrame] = useState(showEmbeddedStyle ? LOGO_PALETTES.length - 1 : 0);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevEmbedId.current = currentRouteKey;
      return;
    }

    // Detect app-to-app switch (showEmbeddedStyle stays true but ID changes)
    const isAppSwitch = showEmbeddedStyle && prevEmbedded.current && currentRouteKey !== prevEmbedId.current;
    prevEmbedId.current = currentRouteKey;

    if (!showEmbeddedStyle) {
      setShouldAnimate(false);
      if (prevEmbedded.current) {
        setTextHidden(true);
        setLogoFrame(LOGO_PALETTES.length - 1);
        let innerRaf: number;
        const raf = requestAnimationFrame(() => {
          innerRaf = requestAnimationFrame(() => {
            setTextHidden(false);
            setLogoFrame(0);
          });
        });
        prevEmbedded.current = false;
        return () => {
          cancelAnimationFrame(raf);
          cancelAnimationFrame(innerRaf);
        };
      }
      setTextHidden(false);
      setLogoFrame(0);
      prevEmbedded.current = false;
      return;
    }

    prevEmbedded.current = true;

    if (isAppSwitch) {
      // Reset animation state first, then re-trigger on next frame
      setShouldAnimate(false);
      const raf = requestAnimationFrame(() => {
        setShouldAnimate(true);
      });
      return () => cancelAnimationFrame(raf);
    }

    setShouldAnimate(true);
    const textRaf = requestAnimationFrame(() => setTextHidden(true));

    let frame = 0;
    let interval: ReturnType<typeof setInterval>;
    const startDelay = setTimeout(() => {
      interval = setInterval(() => {
        frame++;
        if (frame >= LOGO_PALETTES.length - 1) {
          setLogoFrame(LOGO_PALETTES.length - 1);
          clearInterval(interval);
        } else {
          setLogoFrame(frame);
        }
      }, 60);
    }, 300);

    return () => {
      cancelAnimationFrame(textRaf);
      clearTimeout(startDelay);
      clearInterval(interval);
    };
  }, [showEmbeddedStyle, currentRouteKey]);

  const [isLogoHovered, setIsLogoHovered] = useState(false);

  return (
    <div className={`dfp-top-nav__left${showEmbeddedStyle ? ' dfp-top-nav__left--embedded' : ''}`}>
      <a
        href="/"
        className="dfp-top-nav__logo-link"
        onClick={(e) => {
          e.preventDefault();
          navigate('/');
        }}
        onMouseEnter={() => setIsLogoHovered(true)}
        onMouseLeave={() => setIsLogoHovered(false)}
        aria-label="FinSuite Home"
      >
        <LogoIcon paletteIndex={isLogoHovered ? 0 : logoFrame} className="dfp-top-nav__logo-icon" />
        <img src={DFP_LOGO_TEXT} alt="FinSuite" className={`dfp-top-nav__logo-text${textHidden ? ' dfp-top-nav__logo-text--hidden' : ''}`} />
      </a>
      {showEmbeddedStyle && displayAppName && (
        <img
          key={`arrow-${currentRouteKey}`}
          src={ARROW_ICON}
          alt=""
          className={`dfp-top-nav__arrow${shouldAnimate ? ' dfp-top-nav__arrow--slide-in' : ''}`}
        />
      )}
      {showEmbeddedStyle && displayAppName && (
        <a
          key={`app-${currentRouteKey}`}
          href={isEmbedded ? `/all-apps/${embedMatch!.params.id}` : routeConfig?.path || '#'}
          className={`dfp-top-nav__app_text${shouldAnimate ? ' dfp-top-nav__app_text--animated' : ''}`}
          onClick={(e) => {
            if (isEmbedded) {
              e.preventDefault();
              handleEmbeddedAppHomeNavigation();
            } else if (routeConfig?.path === '/ea-huddle') {
              // EA Huddle needs full page reload to reset hook state
              return;
            } else {
              e.preventDefault();
              if (routeConfig?.path) navigate(routeConfig.path);
            }
          }}
          aria-label={`Navigate to ${isEmbedded ? embeddedApp!.name : routeConfig?.navigationText} home`}
        >
          {isEmbedded && embeddedApp?.logoConfig && (
            <span className="dfp-top-nav__app-logo-wrapper">
              <AppLogo logoConfig={embeddedApp.logoConfig} />
            </span>
          )}
          <span className="dfp-top-nav__app-name">{displayAppName}</span>
        </a>
      )}
    </div>
  );
};
